// src/data/obligationRecord.js
//
// THE OBLIGATION OF MEMBERSHIP - Part A of data/YCP_PLAN_LOCAL_RECORDS_AND_DESK_2026-09-22.md, approved by
// Bob 2026-09-22 ("approved"), task list section 346.
//
// SOURCE, in the instrument's own words (PD-16), data/agreements/smart-constitution-2024.source.txt:
//   Article 16 section 3(a), line 4777 - an applicant "shall be notified to appear at a meeting of the local
//   union to take the obligation of membership".
//   Article Ritual, lines 10862-10878 - applicants "may be obligated by the president or presiding officer of
//   the local union either at a local union meeting or meeting of the local union Executive Board", and the
//   member "will sign this same obligation as a matter of record".
// Hence the two meeting types below, the presiding officer, and the signed copy. Not "sworn in": the
// Constitution's word is obligated, and the signature is part of the act.
//
// PURE. No Firestore, no network, no clock - the callable supplies the server time and does the writing, and
// the officer's screen runs the same validation so he is told at the field (directives section XI).
//
// WHAT IS DELIBERATELY NOT HERE:
//   * no fee, no dues, no initiation fee - this platform never handles payment;
//   * no obligation TEXT - the ritual belongs to the local union's meeting, not to a screen;
//   * no second status field on the member. Whether a member is obligated is DERIVED from whether a record
//     exists (obligationStatus below). A stored flag beside the record is the section XI defect.

export const MEETING_TYPES = Object.freeze(['membership_meeting', 'executive_board']);

export const MEETING_LABELS = Object.freeze({
  membership_meeting: 'Meeting of the local union',
  executive_board: 'Meeting of the local union Executive Board',
});

// Bob's decision 2 of 2026-09-22, in order: the date, which meeting, the presiding officer's name, the
// signed copy, and who filed it. `filedBy` is stamped by the server, never sent by a browser.
export const FIELDS = Object.freeze([
  ['memberUid',        'whose obligation this is'],
  ['scopeKey',         "the Local whose records it is filed in - the Local IS the scopeKey (doctrine 5.2)"],
  ['generalCommittee', 'the committee whose General Chairperson reads it'],
  ['obligationDate',   'the date he took it, as yyyy-mm-dd'],
  ['meetingType',      'a meeting of the local union, or of its Executive Board'],
  ['presidingOfficer', 'the president or presiding officer who administered it, by name'],
  ['signedCopyPath',   'the signed obligation, filed in his Local\'s own records'],
]);

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// The presiding officer of a local union is usually NOT an account on this platform, so the name is a
// recorded string. It is a fact about a meeting, not a platform identity, and it is not silently upgraded
// into one.
export function validateObligation(form) {
  const f = form || {};
  const missing = FIELDS.map((r) => r[0]).filter((k) => !String(f[k] == null ? '' : f[k]).trim());
  const meeting = String(f.meetingType || '').trim();
  const badMeeting = meeting !== '' && !MEETING_TYPES.includes(meeting);
  const date = String(f.obligationDate || '').trim();
  const badDate = date !== '' && !DATE_ONLY.test(date);
  // The signed copy lives in the Local's own area, and in no other: locals/<scopeKey>/<file>. A path
  // anywhere else would put a member's signed obligation outside the area the rules scope to his Local.
  const path = String(f.signedCopyPath || '').trim();
  const key = String(f.scopeKey || '').trim();
  const badPath = path !== '' && !(key !== '' && path.startsWith('locals/' + key + '/') && path.length > ('locals/' + key + '/').length);
  return {
    ok: missing.length === 0 && !badMeeting && !badDate && !badPath,
    missing, badMeeting: badMeeting ? meeting : '', badDate: badDate ? date : '', badPath: badPath ? path : '',
  };
}

export function buildObligation({ form, filedByUid, now }) {
  const f = form || {};
  const out = {};
  for (const [k] of FIELDS) out[k] = String(f[k] == null ? '' : f[k]).trim();
  out.filedBy = String(filedByUid || '');     // the account that filed it - the server's answer, not the form's
  out.filedAt = now;                          // the server clock, as every other record here
  return out;
}

// DERIVED, never stored twice. An empty list is 'outstanding' and that is not a fault: Bob's decision 1 is
// that approval opens the account and the obligation is recorded when it happens.
export function obligationStatus(records) {
  return (Array.isArray(records) ? records : []).length > 0 ? 'obligated' : 'outstanding';
}

// WHO MAY FILE ONE. The same spine as applicationApproval.mayDecide: the Local Chairperson whose scopes hold
// that Local, the General Chairperson of that committee, or the administrator. The member may not file his
// own - he signs the paper; he does not write the record about himself (plan Part A3).
export function mayFileObligation(actor, record) {
  const a = actor || {};
  const r = record || {};
  if (a.role === 'admin') return { ok: true, rung: 'admin' };
  if (a.role === 'general_chair') {
    const ok = !!a.committee && a.committee === r.generalCommittee;
    return ok ? { ok: true, rung: 'general_chair' }
              : { ok: false, rung: 'general_chair', reason: 'That obligation belongs to another committee.' };
  }
  if (a.role === 'local_chair') {
    const scopes = Array.isArray(a.scopes) ? a.scopes : (a.scopeKey ? [a.scopeKey] : []);
    const ok = !!r.scopeKey && scopes.indexOf(r.scopeKey) !== -1;
    return ok ? { ok: true, rung: 'local_chair' }
              : { ok: false, rung: 'local_chair', reason: 'That obligation belongs to another Local.' };
  }
  return { ok: false, rung: '', reason: 'Only the Local Chairperson of that Local, his General Chairperson, or the administrator may file an obligation.' };
}

// RULING 208.3 (Bob, 2026-09-22): an obligation still outstanding 60 DAYS after approval is flagged to the
// General Chairperson. Stated here so the number lives in one place, with the reason Bob gave it: the duty to
// convene is the LOCAL's, not the member's, so the flag is never read as discipline of a member.
export const OBLIGATION_FLAG_DAYS = 60;

export function obligationOverdue(approvedAtMs, records, nowMs) {
  if (obligationStatus(records) === 'obligated') return { overdue: false, days: null };
  const a = Number(approvedAtMs);
  const n = typeof nowMs === 'number' ? nowMs : Date.now();
  if (!a || !isFinite(a)) return { overdue: false, days: null };   // unknown is not overdue
  const days = Math.floor((n - a) / 86400000);
  return { overdue: days >= OBLIGATION_FLAG_DAYS, days };
}
